import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAsset, getMyAssetVersion, saveEditedVersion, setActiveVersion, revertAssetVersion, approveAssetVersion, markAssetUsed } from '../utils/api';
import { ArrowLeft, Copy, ExternalLink, Pencil, Save, X, RotateCcw, CheckCircle2 } from 'lucide-react';
import Layout from '../components/Layout';
import { RewriteMenu, STATUS_STYLES, STATUS_LABELS } from '../components/RewriteMenu';

const VERSION_LABELS = { master: 'Original', ai_draft: 'AI Draft', edited: 'Your Edited Version' };

const AssetEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAsset();
    loadBusinessProfile();
    loadVersion();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAsset = async () => {
    try {
      const data = await getAsset(id);
      setAsset(data);
    } catch (error) {
      console.error('Error loading asset:', error);
      setMessage('Error loading asset');
    } finally {
      setLoading(false);
    }
  };

  const loadVersion = async () => {
    try {
      const { data } = await getMyAssetVersion(id);
      setVersion(data);
    } catch (error) {
      console.error('Error loading version:', error);
    }
  };

  const loadBusinessProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/business`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setBusinessProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const replaceMergeFields = (text) => {
    if (!businessProfile || !text) return text;
    return text
      .replace(/\[Company\]/g, businessProfile.company_name || '[Company]')
      .replace(/\[Phone\]/g, businessProfile.phone || '[Phone]')
      .replace(/\[City\]/g, businessProfile.city || '[City]')
      .replace(/\[Offer\]/g, businessProfile.default_offer || '[Offer]')
      .replace(/\[Link\]/g, businessProfile.booking_link || '[Link]');
  };

  const flash = (m) => { setMessage(m); setTimeout(() => setMessage(''), 3000); };

  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      flash('✅ Copied to clipboard!');
      markAssetUsed(id).then(loadVersion).catch(() => {});
    } catch (err) {
      flash('Failed to copy');
    }
  };

  const handleOpenInCanva = () => {
    handleCopyToClipboard(displayContent);
    window.open('https://www.canva.com', '_blank');
    flash('📋 Design brief copied! Paste it in Canva to build your graphic.');
  };

  const switchVersion = async (v) => {
    if (editing) return;
    try {
      const { data } = await setActiveVersion(id, v);
      setVersion(data);
    } catch (e) {
      flash(e.response?.data?.detail || 'Could not switch version');
    }
  };

  const startEdit = () => {
    setDraft(displayContent);
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await saveEditedVersion(id, draft);
      setVersion(data);
      setEditing(false);
      flash('✅ Saved as Your Edited Version');
    } catch (e) {
      flash('Save failed — try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    if (!window.confirm('Delete your edited version and go back to the original? This can\'t be undone.')) return;
    try {
      const { data } = await revertAssetVersion(id);
      setVersion(data);
      setEditing(false);
      flash('Reverted to original.');
    } catch (e) {
      flash('Revert failed — try again.');
    }
  };

  const handleApprove = async () => {
    try {
      const { data } = await approveAssetVersion(id);
      setVersion(data);
      flash('✅ Approved — this version is good to go.');
    } catch (e) {
      flash('Approve failed — try again.');
    }
  };

  if (loading) {
    return (
      <Layout variant="light">
        <div className="flex items-center justify-center h-64">
          <div className="text-light-gray">Loading asset...</div>
        </div>
      </Layout>
    );
  }

  if (!asset) {
    return (
      <Layout variant="light">
        <div className="flex items-center justify-center h-64">
          <div className="text-light-gray">Asset not found</div>
        </div>
      </Layout>
    );
  }

  const isGraphicAsset = ['Graphics', 'Print'].includes(asset.category || asset.asset_category);
  const activeVersion = version?.active_version || 'master';
  const status = version?.status || 'ready';

  const masterContent = asset.content || asset.body || '';
  const rawContent =
    activeVersion === 'edited' ? (version?.edited_content || masterContent)
      : activeVersion === 'ai_draft' ? (version?.ai_draft_content || masterContent)
        : masterContent;
  const displayContent = activeVersion === 'master' ? replaceMergeFields(rawContent) : rawContent;

  const isSMS = (asset.asset_type || asset.asset_name || '').includes('SMS');
  const versionTabs = ['master', ...(version?.has_ai_draft ? ['ai_draft'] : []), ...(version?.has_edited ? ['edited'] : [])];

  return (
    <Layout variant="light">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-light-gray hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft size={20} />
              Back
            </button>
            <h1 className="text-4xl font-bold text-white mb-2">
              {asset.asset_type || asset.asset_name || 'Asset'}
            </h1>
            <div className="flex items-center gap-3">
              <span className="text-light-gray">{asset.asset_category || asset.category}</span>
              <span data-testid="asset-status-badge" className={`text-xs px-3 py-1 rounded-full font-medium ${STATUS_STYLES[status] || STATUS_STYLES.ready}`}>
                {STATUS_LABELS[status] || 'Ready'}
              </span>
            </div>
          </div>
        </div>

        {message && (
          <div className="mb-6 p-4 bg-construction-orange/20 border border-construction-orange rounded-lg text-white" data-testid="asset-editor-message">
            {message}
          </div>
        )}

        {/* Content panel with versions + in-app editing */}
        <div className="card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-2xl font-bold text-white mr-2">{isGraphicAsset ? '📝 Design Brief' : '📄 Content'}</h2>
              {versionTabs.map((v) => (
                <button
                  key={v}
                  onClick={() => switchVersion(v)}
                  disabled={editing}
                  data-testid={`version-tab-${v}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors disabled:opacity-50 ${
                    activeVersion === v
                      ? 'bg-lime/15 border-lime/50 text-lime'
                      : 'bg-black border-divider text-gray hover:text-white'
                  }`}
                >
                  {VERSION_LABELS[v]}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {!editing && (
                <>
                  <button
                    onClick={startEdit}
                    data-testid="asset-edit-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-lime text-black font-bold text-sm rounded-lg hover:bg-lime/90"
                  >
                    <Pencil size={15} /> Edit
                  </button>
                  <RewriteMenu assetId={id} onDone={() => { loadVersion(); flash('✨ AI rewrite ready — review it below.'); }} onError={flash} />
                  {status === 'needs_review' && (
                    <button
                      onClick={handleApprove}
                      data-testid="asset-approve-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-lime/10 border border-lime/40 text-lime text-sm font-bold rounded-lg hover:bg-lime/20"
                    >
                      <CheckCircle2 size={15} /> Approve
                    </button>
                  )}
                  {version?.has_edited && (
                    <button
                      onClick={handleRevert}
                      data-testid="asset-revert-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-divider text-gray text-sm rounded-lg hover:text-white"
                    >
                      <RotateCcw size={14} /> Revert to Original
                    </button>
                  )}
                  <button
                    onClick={() => handleCopyToClipboard(displayContent)}
                    data-testid="asset-copy-btn"
                    className="btn-secondary inline-flex items-center gap-2"
                  >
                    <Copy size={16} />
                    {isGraphicAsset ? 'Copy Design Brief' : 'Copy Content'}
                  </button>
                </>
              )}
            </div>
          </div>

          <p className="text-xs text-muted mb-3" data-testid="version-indicator">
            Viewing: <span className="text-lime font-semibold">{VERSION_LABELS[activeVersion]}</span>
            {activeVersion === 'master' && ' — the shared template. Your edits save as your own version and never change this.'}
            {activeVersion === 'ai_draft' && ' — created by AI from your Brand Profile. Edit it or approve it.'}
            {activeVersion === 'edited' && ' — your own edited copy of this asset.'}
          </p>

          {editing ? (
            <div data-testid="asset-edit-mode">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid="asset-edit-textarea"
                className="w-full min-h-[480px] bg-slate-blue border border-lime/40 rounded-lg p-5 text-light-gray text-sm leading-relaxed focus:border-lime focus:outline-none font-mono"
              />
              <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
                <div className="text-xs text-gray">
                  {draft.length.toLocaleString()} characters
                  {isSMS && draft.length > 160 && (
                    <span className="text-orange ml-2" data-testid="sms-length-warning">
                      ⚠ Over 160 characters — this SMS may send as multiple messages. Still fine to save.
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditing(false)}
                    data-testid="asset-edit-cancel-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 border border-divider text-gray text-sm rounded-lg hover:text-white"
                  >
                    <X size={15} /> Cancel
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={saving}
                    data-testid="asset-edit-save-btn"
                    className="inline-flex items-center gap-2 px-5 py-2 bg-lime text-black font-bold text-sm rounded-lg hover:bg-lime/90 disabled:opacity-50"
                  >
                    <Save size={15} /> {saving ? 'Saving…' : 'Save My Version'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-slate-blue rounded-lg p-6" data-testid="asset-content-display">
              <div className="whitespace-pre-wrap text-light-gray leading-relaxed">
                {displayContent}
              </div>
            </div>
          )}
        </div>

        {/* Canva action for graphic assets */}
        {isGraphicAsset && !editing && (
          <div className="card mt-6 bg-blue-600/10 border-2 border-blue-500/30">
            <h3 className="text-xl font-bold text-white mb-2">🎨 Build This Graphic</h3>
            <p className="text-light-gray mb-4">
              Use the design brief above to create your branded graphic in Canva. Your logo and brand colors will apply automatically in Canva's brand kit.
            </p>
            <button onClick={handleOpenInCanva} className="btn-primary inline-flex items-center gap-2">
              <ExternalLink size={16} />
              Open in Canva
            </button>
          </div>
        )}

        {/* Brand setup nudge */}
        {businessProfile && businessProfile.company_name === '[Company]' && (
          <div className="mt-6 bg-yellow-500/10 border-2 border-yellow-500/50 rounded-lg p-4">
            <p className="text-yellow-400 font-medium mb-2">⚠️ Set up your brand first</p>
            <p className="text-light-gray text-sm mb-3">
              Fill out your business info once to automatically personalize every asset.
            </p>
            <button onClick={() => navigate('/my-brand')} className="btn-secondary text-sm">
              Set Up Brand →
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AssetEditor;
